import React, { useState, useEffect } from 'react';

// 백엔드 /deploy/images 가 반환하는 배포 이미지 항목(deploy-images/manifest.json 기반).
type DeployImage = {
  device: string;      // 섹션 id 와 매칭 (v100q / odroid / x86)
  title?: string;
  filename: string;
  version?: string;
  sizeBytes?: number;
  sha256?: string;
  format?: string;
  notes?: string;
  url: string;         // /deploy/download/<filename>
};

const formatBytes = (n?: number): string => {
  if (!n || n <= 0) return '';
  const gb = n / (1024 * 1024 * 1024);
  if (gb >= 1) return `${gb.toFixed(2)} GB`;
  return `${(n / (1024 * 1024)).toFixed(0)} MB`;
};

// 기기 섹션 안에 렌더되는 다운로드 카드. 이미지가 실제 제공될 때만(백엔드에 파일 존재) 표시.
const DownloadCard: React.FC<{ image: DeployImage }> = ({ image }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div className="my-3 border border-green-200 bg-green-50 rounded-lg p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-800">
            {image.title || '설치 이미지'}
            {image.version && <span className="ml-2 text-xs text-gray-500">버전 {image.version}</span>}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            {image.format || '디스크 이미지'}{image.sizeBytes ? ` · ${formatBytes(image.sizeBytes)}` : ''}
          </div>
        </div>
        <a
          href={image.url}
          className="shrink-0 inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded"
          download
        >
          ⬇ 다운로드
        </a>
      </div>
      {image.notes && <p className="text-xs text-gray-600 mt-2">{image.notes}</p>}
      {image.sha256 && (
        <div className="mt-2 text-[11px] text-gray-500 break-all">
          <span className="font-medium">SHA-256:</span> <code>{image.sha256}</code>
          <button
            type="button"
            onClick={async () => {
              try { await navigator.clipboard.writeText(image.sha256!); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* 무시 */ }
            }}
            className="ml-2 px-1.5 py-0.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded text-[10px]"
          >
            {copied ? '복사됨' : '복사'}
          </button>
          <p className="mt-1 text-gray-400">내려받은 파일이 손상되지 않았는지 확인하려면 위 값과 대조하세요.</p>
        </div>
      )}
    </div>
  );
};

// 일반 사용자용 기기 설치 가이드 — 화면(디지털 사이니지)을 MOc 서비스에 연결하는 3가지 방법.
// 운영자용 상세/SSH 절차는 관리자 페이지(/admin/device-setup)에 별도로 있음.
// 이 페이지는 로그인한 모든 사용자에게 노출된다(admin gating 없음).

type Row = { device: string; install: string; update: string; level: string };

type Block =
  | { kind: 'p'; text: string }
  | { kind: 'note'; text: string }
  | { kind: 'warn'; text: string }
  | { kind: 'steps'; items: string[] }
  | { kind: 'code'; lines: string[] }
  | { kind: 'compare'; rows: Row[] }
  // 섹션 안 소제목 — 한 기기에 설치 방법이 여럿일 때(M1S) 방법 단위로 나눈다.
  | { kind: 'h3'; text: string }
  // 다운로드 카드를 본문 흐름 안(해당 방법 바로 아래)에 놓는다. device = manifest 의 device 키.
  // 이 블록을 쓰는 섹션은 상단 일괄 카드 노출이 꺼진다 — 어떤 파일이 어떤 방법 것인지 자리로 말한다.
  | { kind: 'download'; device: string };

interface Section {
  id: string;
  title: string;
  subtitle?: string;
  badge?: 'available' | 'coming_soon';
  blocks: Block[];
}

const CodeBlock: React.FC<{ lines: string[] }> = ({ lines }) => {
  const [copied, setCopied] = useState(false);
  const text = lines.join('\n');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard 미지원 — 무시 */
    }
  };
  return (
    <div className="relative group my-3">
      <button
        type="button"
        onClick={copy}
        className="absolute top-2 right-2 text-[11px] px-2 py-0.5 bg-gray-700 hover:bg-gray-600 text-gray-100 rounded opacity-0 group-hover:opacity-100 transition"
      >
        {copied ? '복사됨' : '복사'}
      </button>
      <pre className="bg-gray-900 text-gray-100 text-xs leading-relaxed p-3 pr-16 overflow-x-auto rounded">
        <code>{text}</code>
      </pre>
    </div>
  );
};

const CompareTable: React.FC<{ rows: Row[] }> = ({ rows }) => (
  <div className="overflow-x-auto my-3">
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="bg-gray-100 text-gray-700 text-left">
          <th className="px-3 py-2 font-semibold">기기</th>
          <th className="px-3 py-2 font-semibold">설치 방법</th>
          <th className="px-3 py-2 font-semibold">자동 업데이트</th>
          <th className="px-3 py-2 font-semibold">난이도</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-gray-200">
            <td className="px-3 py-2 font-medium text-gray-800">{r.device}</td>
            <td className="px-3 py-2 text-gray-600">{r.install}</td>
            <td className="px-3 py-2 text-gray-600">{r.update}</td>
            <td className="px-3 py-2 text-gray-600">{r.level}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// 코드 예시 안의 `{file:<device>}` 를 manifest 의 실제 파일명으로 바꾼다. 파일명을 본문에 박아 두면
// 이미지를 갱신할 때마다 가이드가 어긋난다(2026-10-07: 지운 지 오래된 파일명이 남아 있었다).
const resolveFile = (images: DeployImage[], device: string): string =>
  images.find((im) => im.device === device)?.filename ?? `<${device} 이미지 파일명>`;
const substituteFiles = (line: string, images: DeployImage[]): string =>
  line.replace(/\{file:([a-z0-9-]+)\}/g, (_m, d: string) => resolveFile(images, d));

const BlockView: React.FC<{ block: Block; images: DeployImage[] }> = ({ block, images }) => {
  switch (block.kind) {
    case 'h3':
      return <h3 className="text-base font-semibold text-gray-800 mt-6 mb-1">{block.text}</h3>;
    case 'download': {
      const hits = images.filter((im) => im.device === block.device);
      if (hits.length === 0) {
        return (
          <div className="my-3 border border-gray-200 bg-gray-50 rounded-lg p-3 text-xs text-gray-500">
            이 방법에 필요한 이미지가 아직 올라오지 않았습니다.
          </div>
        );
      }
      return <>{hits.map((im) => <DownloadCard key={im.filename} image={im} />)}</>;
    }
    case 'p':
      return <p className="text-sm text-gray-700 leading-relaxed my-2">{block.text}</p>;
    case 'note':
      return (
        <div className="text-sm text-blue-800 bg-blue-50 border-l-4 border-blue-300 px-3 py-2 my-3">
          {block.text}
        </div>
      );
    case 'warn':
      return (
        <div className="text-sm text-amber-900 bg-amber-50 border-l-4 border-amber-400 px-3 py-2 my-3">
          ⚠️ {block.text}
        </div>
      );
    case 'steps':
      return (
        <ol className="list-decimal pl-6 my-2 space-y-1 text-sm text-gray-700">
          {block.items.map((it, i) => (
            <li key={i} className="leading-relaxed">{it}</li>
          ))}
        </ol>
      );
    case 'code':
      return <CodeBlock lines={block.lines.map((l) => substituteFiles(l, images))} />;
    case 'compare':
      return <CompareTable rows={block.rows} />;
  }
};

const Badge: React.FC<{ kind: 'available' | 'coming_soon' }> = ({ kind }) =>
  kind === 'available' ? (
    <span className="ml-2 align-middle text-[11px] px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
      지금 이용 가능
    </span>
  ) : (
    <span className="ml-2 align-middle text-[11px] px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full">
      이미지 준비 중
    </span>
  );

const SECTIONS: Section[] = [
  {
    id: 'overview',
    title: '개요 — 어떤 기기를 고를까',
    subtitle: '화면 1대 = 소형 컴퓨터 1대. 아래 3가지 중 하나를 준비해 MOc 에 연결하면 됩니다.',
    blocks: [
      { kind: 'p', text: '매장/공간의 디스플레이(모니터·TV)에 소형 컴퓨터를 연결하고, 그 컴퓨터에 MOc 를 설치하면 화면이 됩니다. 설치가 끝나면 화면에 QR 코드가 뜨고, 휴대폰으로 촬영해 등록하면 바로 재생이 시작됩니다.' },
      { kind: 'compare', rows: [
        { device: 'ODROID M1S (전용 소형기기)', install: '설치 USB + 명령어 한 줄 → 자동설치', update: '앱 + OS (원격 관리)', level: '쉬움' },
        { device: 'ODROID M2 (전용 소형기기)', install: 'SD카드 이미지 굽기 → 전원', update: '앱 + OS (원격 관리)', level: '쉬움' },
        { device: 'Lenovo V100Q (검증 미니PC)', install: '설치 USB 로 부팅 → 자동설치', update: '앱 + OS (원격 관리)', level: '쉬움' },
        { device: '아무 x86 미니PC', install: '미니멀 NixOS + 명령어 한 줄', update: '앱만 (OS 는 고정)', level: '보통' },
      ] },
      { kind: 'note', text: '고정된 기기(ODROID·V100Q)는 우리가 미리 만든 이미지를 쓰기 때문에, 설치가 아주 간단하고 나중에 OS까지 원격으로 자동 업데이트됩니다. 아무 x86 PC 는 기기마다 하드웨어가 달라 미리 이미지를 만들 수 없어, 직접 미니멀 NixOS 를 깐 뒤 명령어 한 줄로 MOc 를 올립니다(이 경우 재생 앱만 자동 업데이트되고 OS 는 설치 시점으로 고정).' },
    ],
  },
  {
    id: 'register',
    title: '공통 마지막 단계 — 화면 QR 등록',
    subtitle: '어느 기기든 설치가 끝나면 이 단계로 마무리됩니다.',
    blocks: [
      { kind: 'steps', items: [
        '설치가 끝나면 화면에 QR 코드가 표시됩니다.',
        '휴대폰으로 MOcombinator 에 로그인합니다.',
        '“스크린 등록” 메뉴에서 카메라로 화면의 QR 코드를 촬영합니다.',
        '등록이 완료되면 잠시 후 배정된 영상이 재생됩니다.',
      ] },
      { kind: 'note', text: '한 계정에 화면 여러 대를 등록할 수 있습니다. 각 화면은 QR 이 다르므로 화면별로 한 번씩 촬영해 등록하세요.' },
      { kind: 'note', text: '첫 부팅에 화면이 켜지고 QR 이 뜹니다 — 아직 배정된 영상이 하나도 없어도 켜집니다. 기기를 먼저 켜고 모니터를 나중에 켜도 되고, 쓰던 중에 모니터를 껐다 켜도 화면이 스스로 돌아옵니다.' },
      { kind: 'p', text: 'Wi-Fi 로 연결하기 — 유선 랜을 꽂으면 아무것도 할 필요가 없습니다. 랜선을 쓸 수 없는 자리라면 기기에 USB 키보드를 꽂고 아래 둘 중 하나로 연결합니다.' },
      { kind: 'steps', items: [
        '키보드에서 W 키를 누르면 화면에 Wi-Fi 목록이 뜹니다. ↑↓ 로 공유기를 고르고 Enter, 비밀번호를 입력한 뒤 Enter 로 연결합니다. (다시 W 를 누르면 닫힙니다.)',
        '또는 공유기의 WPS 버튼을 누릅니다 — 기기가 자동으로 잡습니다. 공유기에 따라 버튼이 인식되지 않을 수 있는데(화면에 “WPS timeout” 이 뜹니다), 그때는 위 W 키 방법을 쓰세요.',
      ] },
      { kind: 'note', text: '연결되면 화면 아래쪽의 네트워크 표시가 사라집니다. 비밀번호에 기호(!@#$ 등)가 있어도 그대로 입력하면 됩니다.' },
      { kind: 'note', text: '영업이 끝나면 화면만 꺼 두셔도 됩니다 — 스크린 관리의 리모컨에서 “화면 끄기”를 누르면 TV 가 대기모드로 들어갑니다. 기기는 켜져 있으므로 다음 날 “화면 켜기”로 바로 되돌립니다(전원을 내리면 사람이 현장에 가야 다시 켤 수 있습니다).' },
    ],
  },
  {
    id: 'odroid-m1s',
    title: 'A. ODROID M1S — 전용 소형기기',
    subtitle: '설치 방법이 두 가지입니다. A-1 USB 자동 설치(기본·권장) 또는 A-2 OTG 직접 굽기(리눅스). 쓰는 파일이 서로 다르니 고른 방법 아래의 다운로드만 받으세요.',
    badge: 'available',
    blocks: [
      { kind: 'p', text: '공통 준비물: ODROID M1S 본체, HDMI 케이블, 모니터, 랜선, 전원 어댑터. 여기에 A-1 은 USB 메모리와 USB 키보드, A-2 는 microSD 카드와 리눅스 PC·Micro-USB 데이터 케이블이 더 필요합니다.' },
      { kind: 'warn', text: 'A-1 은 설치 이미지(.img.xz) 1개, A-2 는 OTG 부트 이미지(.img) + 커스터머 이미지(.img.zst) 2개를 씁니다. 서로 바꿔 쓰면 설치가 되지 않습니다.' },

      { kind: 'h3', text: 'A-1. USB 로 자동 설치 (기본·권장)' },
      { kind: 'p', text: '설치 USB 와 명령어 한 줄이면 내장 저장소(eMMC)에 스스로 설치하고 꺼집니다. USB 한 개로 여러 대 반복 설치할 수 있습니다. 필요한 파일은 아래 1개입니다.' },
      { kind: 'download', device: 'odroid-m1s' },
      { kind: 'steps', items: [
        '위 “다운로드” 버튼으로 M1S 설치 이미지(.img.xz)를 내려받아, balenaEtcher(또는 Rufus 등)로 USB 메모리(8GB 이상)에 굽습니다. (.xz 는 자동으로 풀려서 구워집니다.)',
        'M1S 에 모니터(HDMI)·키보드·랜선·전원을 연결해 켭니다. 새 제품이면 첫 화면에서 “Ubuntu Server” 설치를 선택해 기본 OS 로 한 번 부팅합니다(몇 분 소요).',
        '기본 우분투에 로그인합니다 (아이디 odroid / 비밀번호 odroid).',
        '설치 USB 를 꽂고, 아래 명령어 한 줄을 입력합니다.',
        '자동으로 재부팅되며 화면에 설치 진행이 표시되고, 약 3~5분 뒤 “설치 완료” 안내와 함께 기기가 스스로 꺼집니다. (설치 내용은 자동으로 검증됩니다.)',
        '꺼진 뒤 USB 를 제거하고 전원을 다시 연결합니다 — 이제 내장 저장소(eMMC)에서 부팅합니다.',
        '화면에 QR 코드가 나타나면 위 “공통 마지막 단계”대로 등록합니다.',
      ] },
      { kind: 'code', lines: [ 'curl -sL https://mocombinator.com/static/device-setup/m1s-usb-install.sh | sudo bash' ] },
      { kind: 'note', text: '설치 USB 는 소모되지 않습니다 — 같은 USB 를 다음 M1S 에 꽂고 같은 명령어를 실행하면 똑같이 자동 설치됩니다(대량 설치용).' },
      { kind: 'note', text: '만약 설치 화면에 빨간 “실패” 메시지가 보이면 기기는 켜진 채 유지됩니다 — USB 를 다시 굽거나 다른 USB 로 재시도하세요(내장 저장소는 검증 통과 전에는 완료 처리되지 않습니다).' },
      { kind: 'note', text: '(참고) 케이스를 열어 microSD 슬롯을 쓸 수 있다면 더 간단합니다: 같은 설치 이미지를 microSD 에 굽고 꽂아서 켜기만 하면, 우분투/명령어 없이 곧바로 자동 설치가 시작됩니다. (기본 케이스는 SD 슬롯이 가려져 있어 USB 방법을 안내합니다.)' },
      { kind: 'note', text: '(선택·리눅스) balenaEtcher 대신 dd 로도 설치 USB 를 구울 수 있습니다. .img.xz 는 압축 파일이라 dd 에 그대로 넣으면 안 되고, 아래처럼 풀면서 바로 씁니다(압축 해제 시 약 6.5GB — USB 는 8GB 이상). 파이프로 읽을 때 iflag=fullblock 이 없으면 dd 가 블록을 잘게 잘라 읽어 느려집니다. sdX 는 대상 USB(또는 microSD) 장치명이며, 틀리면 다른 디스크를 덮어쓰니 lsblk 로 반드시 확인하세요:' },
      { kind: 'code', lines: [
        'lsblk -o NAME,SIZE,TRAN,MODEL          # 대상 장치 확인 (sdX 를 여기서 고른다)',
        'xz -dc {file:odroid-m1s} | sudo dd of=/dev/sdX bs=4M iflag=fullblock oflag=direct status=progress conv=fsync',
        'sync                                    # 캐시까지 내려간 뒤 뽑는다',
      ] },

      { kind: 'h3', text: 'A-2. OTG 로 내장 저장소(eMMC)에 직접 굽기 (리눅스·선택)' },
      { kind: 'p', text: '우분투 설치도, 설치 USB 도 필요 없어 가장 빠릅니다. Hardkernel 이 제공하는 EMMC2UMS 부트 이미지를 microSD 에 굽고, 그 SD 로 M1S 를 부팅하면 eMMC 가 PC 에 USB 저장장치로 붙습니다 — PC 에서 그 장치에 커스터머 이미지를 씁니다. 케이스를 열어 microSD 슬롯을 쓸 수 있어야 합니다. 필요한 파일은 아래 2개 모두입니다.' },
      { kind: 'download', device: 'odroid-m1s-otg' },
      { kind: 'download', device: 'odroid-m1s-emmc' },
      { kind: 'steps', items: [
        'OTG 부트 이미지(EMMC2UMS, 3MB)를 microSD 에 굽습니다 (아래 ①, 아무 SD 나 가능).',
        'M1S 에 그 SD 를 꽂고, PC 와 Micro-USB(OTG) 케이블로 연결한 뒤 전원을 넣습니다. ⚠️ 충전 전용 케이블은 데이터선이 없어 인식되지 않습니다 — 데이터 케이블을 쓰세요.',
        'PC 에서 `lsblk` 로 새 장치를 확인합니다 (아래 ②). MODEL 에 “UMS disk 0”, 크기 약 58GB 로 보이면 성공입니다.',
        'PC 가 그 장치를 자동으로 마운트했다면 먼저 해제합니다 — 마운트된 채로 구우면 실패하거나 내용이 깨집니다. (`lsblk` 의 MOUNTPOINTS 가 비어 있어야 합니다.)',
        '그 장치에 커스터머 이미지(.img.zst)를 굽습니다 (아래 ③). 약 4.4GB 를 쓰므로 2~3분 걸립니다.',
        '다 구워지면 SD 를 빼고 전원을 다시 넣습니다 — 이제 eMMC 로 부팅합니다.',
        '화면에 QR 코드가 나타나면 위 “공통 마지막 단계”대로 등록합니다.',
      ] },
      { kind: 'code', lines: [
        'sudo dd if={file:odroid-m1s-otg} of=/dev/sdX bs=1M status=progress conv=fsync   # ① SD 에 OTG 부트 이미지',
        'lsblk -o NAME,SIZE,TRAN,MODEL          # ② M1S 부팅 후 — MODEL 이 "UMS disk 0" 인 장치를 찾는다',
        'zstdcat {file:odroid-m1s-emmc} | sudo dd of=/dev/sdY bs=4M iflag=fullblock oflag=direct status=progress conv=fsync && sync   # ③ 그 장치(sdY)에 굽기',
      ] },
      { kind: 'warn', text: '①의 sdX 는 microSD, ③의 sdY 는 UMS 로 붙은 eMMC 로 **서로 다른 장치**입니다. 헷갈리면 SD 를 지우거나 PC 디스크를 덮어쓸 수 있으니 각 단계마다 lsblk 로 확인하세요.' },
      { kind: 'note', text: '여러 대를 준비할 때: OTG 부트 SD 는 **한 번만 구우면 계속 재사용**합니다(①은 최초 1회). 장비마다 SD 꽂고 → 부팅 → 굽기 → SD 빼기만 반복하면 됩니다. 같은 이미지를 써도 각 기기가 첫 부팅에 고유 신원을 스스로 만들어 서로 충돌하지 않습니다.' },
      { kind: 'note', text: '굽기가 끝난 뒤 화면이 검게 보이면 잠시 기다리세요 — 첫 부팅은 저장소 확장과 초기 설정 때문에 1~2분 걸립니다. 그 뒤 QR 이 뜹니다.' },

      { kind: 'note', text: '등록 후에는 기기를 그대로 두면 됩니다. 어느 방법으로 설치했든 재생 앱과 운영체제(OS)를 MOc 가 원격으로 자동 관리합니다. 유선 랜을 꽂으면 자동으로 인터넷에 연결되고, Wi-Fi 만 쓸 경우 설치 후 화면 안내대로 연결합니다.' },
    ],
  },
  {
    id: 'odroid',
    title: 'A-2. ODROID M2 — 가장 쉬운 전용 기기',
    subtitle: '전원만 연결하면 되는 손바닥 크기 전용 기기. 앱·OS 모두 원격 자동 관리.',
    badge: 'available',
    blocks: [
      { kind: 'p', text: '준비물: ODROID M2 본체, microSD 카드(16GB 이상), 카드 리더기, HDMI 케이블, 모니터, 랜선(또는 설치 후 Wi-Fi), 전원 어댑터.' },
      { kind: 'steps', items: [
        '아래 “다운로드” 버튼으로 ODROID M2 전용 이미지(.img.xz)를 내려받습니다.',
        'balenaEtcher(또는 Rufus 등 이미지 굽기 프로그램)로 내려받은 파일을 그대로 microSD 카드에 굽습니다. (balenaEtcher 는 .xz 압축을 자동으로 풀어 굽습니다 — 따로 압축 해제할 필요 없음.)',
        'microSD 를 ODROID 에 꽂고, 모니터(HDMI)·랜선·전원을 연결해 켭니다. (부트로더가 이미지에 포함돼 있어 추가 작업 없이 바로 부팅합니다.)',
        '잠시 뒤 화면에 QR 코드가 나타나면 위 “공통 마지막 단계”대로 등록합니다.',
      ] },
      { kind: 'note', text: '등록 후에는 기기를 그대로 두면 됩니다. 재생 앱은 물론 운영체제(OS)까지 MOc 가 원격으로 안전하게 자동 업데이트합니다 — 별도 조작이 필요 없습니다.' },
      { kind: 'note', text: '같은 이미지를 여러 microSD 에 구워(또는 같은 SD 를 재사용해) 여러 대를 설치할 수 있습니다 — 각 기기는 첫 부팅에 고유 신원을 스스로 만들어 서로 충돌하지 않습니다.' },
      { kind: 'note', text: 'ODROID M2 는 부팅 스위치가 SD 카드 우선으로 설정돼 있어야 합니다(대부분 기본값). 유선 랜을 꽂으면 자동으로 인터넷에 연결되고, Wi-Fi 만 쓸 경우 설치 후 화면 안내대로 연결합니다.' },
      { kind: 'note', text: '(선택) 리눅스에 익숙하다면 balenaEtcher 대신 bmaptool 로도 구울 수 있습니다. 내려받은 .img.xz 를 압축 해제 없이 바로 씁니다 — sdX 는 대상 microSD 장치명이며, 틀리면 다른 디스크를 덮어쓰니 반드시 확인하세요:' },
      { kind: 'code', lines: [ 'sudo bmaptool copy {file:odroid} /dev/sdX' ] },
    ],
  },
  {
    id: 'v100q',
    title: 'B. Lenovo V100Q — 검증된 미니PC',
    subtitle: '성능이 검증된 고정 모델. ODROID 처럼 앱·OS 모두 원격 자동 관리.',
    badge: 'available',
    blocks: [
      { kind: 'p', text: '준비물: Lenovo V100Q 본체, USB 메모리(16GB 이상), HDMI 모니터, 랜선(또는 설치 후 Wi-Fi).' },
      { kind: 'steps', items: [
        '아래 “다운로드” 버튼으로 V100Q 설치 USB 이미지(.iso)를 내려받습니다.',
        'balenaEtcher(또는 Rufus 등 이미지 굽기 프로그램)로 내려받은 .iso 를 USB 메모리에 굽습니다.',
        'V100Q 에 USB 를 꽂고, 부팅 메뉴(보통 F7/F12)에서 USB 로 부팅합니다.',
        '화면에 설치 대상 내장 디스크가 표시됩니다. 디스크를 선택하고 확인하면 자동으로 설치됩니다.',
        '설치가 끝나면 USB 를 빼고 재부팅합니다.',
        '화면에 QR 코드가 나타나면 위 “공통 마지막 단계”대로 등록합니다.',
      ] },
      { kind: 'note', text: 'V100Q 는 하드웨어가 고정된 검증 모델이라, ODROID 와 동일하게 운영체제(OS)까지 MOc 가 원격으로 자동 업데이트합니다. 한 번 설치하면 이후 관리가 필요 없습니다.' },
      { kind: 'note', text: '유선 랜을 꽂으면 자동으로 인터넷에 연결됩니다. Wi-Fi 만 쓸 경우, 설치 후 화면 안내에 따라 Wi-Fi 를 선택·연결하세요.' },
      { kind: 'note', text: '(선택) 리눅스에 익숙하다면 balenaEtcher 대신 dd 로도 USB 를 구울 수 있습니다 — sdX 는 대상 USB 장치명이며, 틀리면 다른 디스크를 덮어쓰니 반드시 확인하세요:' },
      { kind: 'code', lines: [ 'sudo dd if=moc-v100q-installer-20260724.iso of=/dev/sdX bs=4M status=progress conv=fsync' ] },
    ],
  },
  {
    id: 'x86',
    title: 'C. 아무 x86 미니PC — 명령어 한 줄로 설치',
    subtitle: '갖고 있는(또는 새로 산) 아무 x86 미니PC 에 직접 설치. 지금 바로 가능합니다.',
    badge: 'available',
    blocks: [
      { kind: 'p', text: '준비물: x86 미니PC, 설치용 모니터·키보드, USB 메모리(NixOS 설치용), 인터넷(랜선 권장).' },
      { kind: 'p', text: '① 먼저 “미니멀 NixOS”라는 운영체제를 설치합니다. (nixos.org 의 Minimal ISO 를 USB 로 구워 부팅 → 안내에 따라 설치 → 재부팅.)' },
      { kind: 'p', text: '② 설치된 NixOS 에 로그인한 뒤, 아래 한 줄을 실행하면 MOc 가 자동으로 설치·구성됩니다:' },
      { kind: 'code', lines: [
        'curl -fsSL https://mocombinator.com/static/selfinstall.sh | sudo bash',
      ] },
      { kind: 'steps', items: [
        '명령을 실행하면 필요한 구성요소를 내려받아 설치합니다(수 분 소요 — 인터넷 속도에 따라 더 걸릴 수 있음).',
        '설치가 끝나면 화면에 QR 코드가 나타납니다.',
        '위 “공통 마지막 단계”대로 등록하면 재생이 시작됩니다.',
      ] },
      { kind: 'note', text: '이 방식은 재생 앱이 자동으로 최신으로 업데이트됩니다. 다만 운영체제(OS)는 설치한 시점으로 고정됩니다(고정 기기인 ODROID·V100Q 와의 차이). 대부분의 매장 사용에는 문제가 없습니다.' },
      { kind: 'warn', text: '미니멀 NixOS 설치(①)에는 약간의 컴퓨터 지식(USB 부팅·디스크 파티션)이 필요합니다. 설치가 어렵게 느껴지면, 준비되는 대로 전용 기기(ODROID·V100Q, 위 A·B)를 이용하시길 권합니다.' },
    ],
  },
];

const DeviceGuide: React.FC = () => {
  const [images, setImages] = useState<DeployImage[]>([]);

  useEffect(() => {
    fetch('/deploy/images', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((res) => {
        if (res?.success && Array.isArray(res.data?.images)) setImages(res.data.images);
      })
      .catch(() => { /* 목록 로드 실패 — 가이드는 그대로 노출 */ });
  }, []);

  // 섹션 하나에 이미지가 여럿일 수 있다. `<섹션id>` 또는 `<섹션id>-<변형>` 을 그 섹션 것으로 본다.
  // 단, 본문에 `download` 블록을 둔 섹션(M1S)은 카드를 방법별 자리에 직접 놓으므로 상단 일괄 노출을 끈다.
  const imagesFor = (sectionId: string) =>
    images.filter((im) => im.device === sectionId || im.device.startsWith(`${sectionId}-`));

  return (
    <div className="p-4 md:p-6 max-w-4xl">
      <h1 className="text-2xl font-bold mb-1">화면 기기 설치 가이드</h1>
      <p className="text-sm text-gray-500 mb-6">
        디스플레이에 연결할 소형 컴퓨터를 준비해 MOc 서비스에 연결하는 방법입니다. 기기에 따라 절차가 다릅니다.
      </p>

      {/* 목차 */}
      <nav className="flex flex-wrap gap-2 mb-6">
        {SECTIONS.map((s) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            className="text-xs px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded"
          >
            {s.title}
          </a>
        ))}
      </nav>

      {SECTIONS.map((s) => (
        <section key={s.id} id={s.id} className="mb-8 scroll-mt-4">
          <h2 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-1 mb-1">
            {s.title}
            {s.badge && <Badge kind={s.badge} />}
          </h2>
          {s.subtitle && <p className="text-xs text-gray-400 mb-2">{s.subtitle}</p>}
          {!s.blocks.some((b) => b.kind === 'download') &&
            imagesFor(s.id).map((im) => <DownloadCard key={im.filename} image={im} />)}
          {s.blocks.map((b, i) => (
            <BlockView key={i} block={b} images={images} />
          ))}
        </section>
      ))}

      {/* 라이선스 고지 */}
      <footer className="mt-10 pt-4 border-t border-gray-200 text-xs text-gray-500 leading-relaxed">
        <p className="font-medium text-gray-600 mb-1">라이선스</p>
        <p>
          이곳에서 내려받는 모든 소프트웨어와 설치 이미지는 <span className="font-medium">MOc 서비스를 이용하기 위한 목적으로만</span> 사용할 수 있습니다.
          MOc 서비스와 무관한 용도로의 복제·배포·개조·역설계·재사용은 허용되지 않습니다.
        </p>
      </footer>
    </div>
  );
};

export default DeviceGuide;
